import React, { useMemo, useState } from "react";
import { colors, radius, spacing, typography, cardBase } from "../../utils/designTokens";
import { useIsMobile } from "../../hooks/useResponsive";
import { useDesarrolladorasData } from "../../hooks/useDesarrolladorasData";
import { auth } from "../../firebase";
import { STATUS_LABELS } from "../../utils/statusColors";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import AppSelect from "../../components/AppSelect";
import DesarrolladoraFicha from "../../components/DesarrolladoraFicha";
import { estaBloqueado, estaLibre, fmtFechaLiberacion } from "../../utils/crmAsignacion";
import { FaBuilding, FaSearch, FaPlus, FaLock, FaMapMarkerAlt } from "react-icons/fa";

const CRM_STATES = ["sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado"];
const CRM_OPTS = [{ value: "", label: "CRM: cualquiera" }, ...CRM_STATES.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))];
const ASIGN_OPTS = [
  { value: "todas", label: "Todas" },
  { value: "mias", label: "Mías" },
  { value: "libres", label: "Libres" },
];

/**
 * CRM de desarrolladoras (comercial + superadmin). Igual que Prospección pero sin
 * mapa: lista buscable de desarrolladoras con su estado CRM y asignación, más el
 * alta de nuevas. Toca una tarjeta → ficha con el mismo bloqueo de 60 días.
 */
export default function Desarrolladoras() {
  const isMobile = useIsMobile();
  const { desarrolladoras, comerciales, loading, upsertLocal } = useDesarrolladorasData();
  const emailLc = String(auth.currentUser?.email || "").toLowerCase();

  const [busq, setBusq] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [fAsign, setFAsign] = useState("todas");
  const [ficha, setFicha] = useState(undefined); // undefined = cerrada, null = nueva, obj = editar

  const filtradas = useMemo(() => {
    const q = busq.trim().toLowerCase();
    return desarrolladoras
      .filter((d) => {
        if (fEstado && (d.estado || "sin_contactar") !== fEstado) return false;
        if (fAsign === "mias" && String(d.asignadoEmail || "").toLowerCase() !== emailLc) return false;
        if (fAsign === "libres" && !estaLibre(d)) return false;
        if (q) {
          const hay = `${d.nombre || ""} ${d.email || ""} ${d.ciudadZona || ""} ${d.asignadoA || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")));
  }, [desarrolladoras, busq, fEstado, fAsign, emailLc]);

  return (
    <div style={{ padding: isMobile ? 16 : spacing[6], maxWidth: 1100, margin: "0 auto" }}>
      {/* Filtros */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr auto", gap: spacing[2], marginBottom: spacing[3] }}>
        <div style={{ position: "relative", gridColumn: isMobile ? "1 / -1" : "auto" }}>
          <FaSearch size={13} color={colors.gray400} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={busq} onChange={(e) => setBusq(e.target.value)} placeholder="Buscar por nombre, email, zona…" style={searchInput} aria-label="Buscar desarrolladora" />
        </div>
        <AppSelect value={fEstado} onChange={setFEstado} options={CRM_OPTS} ariaLabel="Estado CRM" />
        <AppSelect value={fAsign} onChange={setFAsign} options={ASIGN_OPTS} ariaLabel="Asignación" />
        <button onClick={() => setFicha(null)} style={{ ...addBtn, gridColumn: isMobile ? "1 / -1" : "auto" }}>
          <FaPlus size={12} /> Nueva desarrolladora
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando desarrolladoras…</div>
      ) : filtradas.length === 0 ? (
        <EmptyState icon={FaBuilding} title="Sin desarrolladoras" description="No hay resultados con estos filtros. Probá limpiar la búsqueda o cargá una nueva." />
      ) : (
        <div style={{ display: "grid", gap: spacing[2] }}>
          {filtradas.map((d) => (
            <DevCard key={d.id} d={d} onClick={() => setFicha(d)} />
          ))}
        </div>
      )}

      <DesarrolladoraFicha
        open={ficha !== undefined}
        onClose={() => setFicha(undefined)}
        dev={ficha || null}
        comerciales={comerciales}
        onSaved={(id, data) => upsertLocal(id, data)}
      />
    </div>
  );
}

function DevCard({ d, onClick }) {
  const bloqueado = estaBloqueado(d);
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={{ ...cardBase, padding: spacing[3], cursor: "pointer", display: "flex", alignItems: "center", gap: spacing[3] }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {d.nombre || "Sin nombre"}
        </div>
        <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "flex", gap: spacing[2], flexWrap: "wrap", marginTop: 2 }}>
          {d.ciudadZona && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><FaMapMarkerAlt size={10} /> {d.ciudadZona}</span>}
          {d.email && <span>· {d.email}</span>}
        </div>
        {d.asignadoA && (
          <div style={{ fontSize: typography.sizes.sm, marginTop: 3, color: bloqueado ? colors.primary : colors.gray600 }}>
            {bloqueado ? <><FaLock size={10} /> {d.asignadoA} · se libera {fmtFechaLiberacion(d)}</> : `${d.asignadoA} · libre (venció)`}
          </div>
        )}
      </div>
      <StatusBadge status={d.estado || "sin_contactar"} size="sm" />
    </div>
  );
}

const searchInput = {
  width: "100%", height: 42, padding: "0 12px 0 34px", boxSizing: "border-box",
  border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
  fontSize: typography.sizes.base, fontFamily: typography.fontFamily, background: colors.white, color: colors.gray900,
};
const addBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, height: 42,
  padding: "0 16px", border: "none", borderRadius: radius.sm, background: colors.primary, color: colors.white,
  cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit", whiteSpace: "nowrap",
};
